import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Label } from '@fluentui/react';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import {
  Caption1Strong,
  Combobox,
  makeStyles,
  mergeClasses,
  Option,
} from '@fluentui/react-components';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';

import { callToActionOptions, customTextOption } from './constants';

const useCallToActionStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  select: {
    width: '100%',
  },
  input: {
    width: '100%',
  },
  customTextContainer: {
    '& .text-input-container': {
      width: '100%',
    },
  },
});

export const CallToAction = ({
  cta,
  i18n,
  label,
  inlinePopup,
  labelAction,
  placeholder,
  onChangeCta,
  withCustomText,
  ctaValidations,
  helpTopic,
  helpLabel,
}) => {
  const classes = useCallToActionStyles();
  const availableOptions = withCustomText ? callToActionOptions : callToActionOptions.slice(1);
  const isCustomTextOptionSelected = withCustomText ? cta && (cta === callToActionOptions[0] || !callToActionOptions.includes(cta)) : false;

  const [selectedOption, setSelectedOption] = React.useState(isCustomTextOptionSelected ? callToActionOptions[0] : cta);
  const [customInputText, setCustomInputText] = React.useState(isCustomTextOptionSelected ? cta : '');

  const handleSelect = (_ev, data) => {
    if (!data || !data.optionText) return;
    setSelectedOption(data.optionText);
    if (withCustomText) {
      onChangeCta(data.optionText === callToActionOptions[0] ? customInputText || '' : data.optionText);
    } else {
      onChangeCta(data.optionText);
    }
  };

  const handleInput = ({ data }) => {
    setCustomInputText(data.callToAction);
  };

  const ctaSchema = useMemo(() => ({
    type: 'object',
    properties: {
      callToAction: {
        type: 'string',
        maxLength: ctaValidations.maxLength,
        minLength: ctaValidations.minLength || 0,
      },
    },
  }), [ctaValidations.maxLength, ctaValidations.minLength]);

  return (
    <div className={mergeClasses('display-ads-call-to-action', classes.root)}>
      <div className={mergeClasses('display-ads-call-to-action-label', classes.label)}>
        {label &&
        <Label>
          { label }
          { helpTopic && (
          <HelpPopupInline
            topic={helpTopic}
            aria-label={helpLabel}
          />
        )}
        </Label>}
        {labelAction}
      </div>
      <Combobox
        onSelect={handleSelect}
        inlinePopup={inlinePopup}
        placeholder={placeholder}
        className={mergeClasses('display-ads-call-to-action-select', classes.select)}
        value={i18n.getString(selectedOption)}
        selectedOptions={[selectedOption]}
        onOptionSelect={handleSelect}
      >
        {availableOptions.map(option => (
          <Option
            key={option}
            value={option}
            id={option.replace(/\s/g, '-').toLowerCase()}
          >
            {i18n.getString(option)}
          </Option>
        ))}
      </Combobox>
      {(isCustomTextOptionSelected || selectedOption === customTextOption) && (
        <div className={mergeClasses('display-ads-call-to-action-input', classes.customTextContainer)}>
          <RemainingCharsValidatedTextField
            value={customInputText}
            validationProps={{
              onValidate: handleInput,
              field: 'callToAction',
              schema: ctaSchema,
              alwaysShowError: true,
            }}
            onRenderLabel={() => (
              <Label>
                <Caption1Strong>{i18n.getString(_TL_('Custom action text'))}</Caption1Strong>
              </Label>)}
            onBlur={() => customInputText && onChangeCta(customInputText)}
            i18n={i18n}
          />
        </div>
      )}
    </div>
  );
};

CallToAction.propTypes = {
  i18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
  label: PropTypes.string,
  cta: PropTypes.string,
  ctaValidations: PropTypes.shape({
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
  }),
  inlinePopup: PropTypes.bool,
  labelAction: PropTypes.node,
  placeholder: PropTypes.string,
  onChangeCta: PropTypes.func,
  withCustomText: PropTypes.bool,
  helpTopic: PropTypes.string,
  helpLabel: PropTypes.string,
};

CallToAction.defaultProps = {
  label: null,
  cta: null,
  ctaValidations: {
    minLength: 0,
    maxLength: 20,
  },
  inlinePopup: false,
  placeholder: null,
  labelAction: null,
  withCustomText: true,
  onChangeCta: () => {},
  helpTopic: null,
  helpLabel: null,
};
